/**
 * Does a directed path already exist from `from` to `to` in `adj`?
 * Iterative DFS with a visited set -- recharts' own Sankey layout walker
 * (updateDepthOfTargets) recurses with NO visited set, so any cycle we let
 * through blows its call stack (RangeError) or, for a cycle with no pure
 * source node, silently lays out zero rects. This function is what keeps
 * cycles out of the graph we hand it in the first place.
 */
function pathExists(adj, from, to) {
  if (from === to) return true;
  const seen = new Set([from]);
  const stack = [from];
  while (stack.length) {
    const node = stack.pop();
    for (const next of adj.get(node) || []) {
      if (next === to) return true;
      if (!seen.has(next)) {
        seen.add(next);
        stack.push(next);
      }
    }
  }
  return false;
}

/**
 * Recharts Sankey needs nodes as an array and links as index references.
 *
 * build_paths() emits one from->to transition per consecutive first-action
 * pair PER ATHLETE, and different athletes build their profile in different
 * orders -- athlete A may go photo->video while athlete B goes video->photo.
 * Both edges land in the same transitions list, and a cycle (a 2-cycle from
 * a reciprocal pair, or a longer one like A->B->C->A) is fatal to recharts'
 * layout code (see the pathExists docstring above). Self-loops were already
 * filtered; this additionally greedily builds a DAG: edges are considered
 * strongest-first (count desc, then from/to name for a deterministic tie
 * break so identical runs render identically) and an edge is kept only if
 * it doesn't close a path back to its own source. Dropped edges are never
 * silently discarded -- droppedCount is returned so the caller can tell the
 * reader data was hidden.
 */
/**
 * Container height for a Sankey diagram, driven by node count.
 *
 * recharts computes a per-column `yRatio = (contentHeight - (n-1)*nodePadding)
 * / sum(values)` for the widest column, where contentHeight is the container
 * height minus its default 5px top+bottom margins. At a fixed height, more
 * nodes means less room per node, and past a threshold `yRatio` goes negative
 * -- recharts still renders the node rects with that negative height, so
 * nothing paints. Measured at nodePadding=24 in real headless Chrome (see the
 * 2026-08-29 final review): a fixed 260px container renders fine at 6 nodes
 * (~21.7px/rect), is down to ~3.4px at 10 (invisible), and is NEGATIVE at 12
 * (-1.2px) and 20 (-10.3px) -- so nothing draws, but `links.length > 0` is
 * still true and the empty-state message never fires.
 *
 * This scales the container with node count instead. `minHeight` is the
 * height the CALLING chart used before this helper existed -- it's a
 * per-call-site floor, not a shared constant, because two charts replaced
 * two different fixed heights (260 and 400). Never go below the minHeight
 * the caller passes in; that would shrink a chart that had no defect.
 * Above the floor this scales linearly: e.g. with minHeight=260, 12 nodes
 * gets 332px (~4.8px/rect), 20 nodes gets 540px (~3.7px/rect) -- always
 * positive.
 */
function sankeyHeight(nodeCount, minHeight) {
  return Math.max(minHeight, (nodeCount || 0) * 26 + 20);
}

/**
 * Vertical gap between node rectangles in a Sankey diagram, driven by node
 * count -- companion to sankeyHeight above.
 *
 * recharts' per-column yRatio (chart/Sankey.js:173) is
 * `(H - 10 - (n-1)*nodePadding) / sum(values)`, so nodePadding is pure
 * overhead subtracted from the height before it's divided among the node
 * rects. A fixed nodePadding=24 was fine at the ~6-node diagrams this was
 * designed against, but at higher node counts -- which sankeyHeight already
 * keeps at a positive, scaled height -- that overhead eats an increasing
 * share of the column, leaving hair-thin bars (measured ~5.2px at 20 nodes,
 * ~3.5px at 40, down to 0.26px on a skewed distribution).
 *
 * The owner's call: shrink the gap as node count grows, never truncate flows
 * and never let the chart grow unboundedly to compensate. This function is
 * the shrink half of that; sankeyHeight is the (bounded) grow half.
 *
 * Curve: hold the current, already-good 24px through FULL_AT nodes (6 --
 * matches the node count the current look was tuned against, and must not
 * regress). Linearly ramp down to an 8px floor by FLOOR_AT nodes (30 --
 * comfortably before the 40-node case this was measured against, so the
 * worst case sits flat at the floor rather than mid-ramp). 8px was chosen
 * because it's the smallest gap that still reads as a visual separation
 * between adjacent node rects in a headless render at these heights; below
 * that the bars start to visually merge into one block regardless of their
 * own height. Linear (not a step or a curve with more inflection points)
 * because the two anchor points are the only two we have real evidence for
 * (6 = current good state, 30ish = floor is warranted) -- anything fancier
 * would be curve-fitting without more data points.
 */
function sankeyNodePadding(nodeCount) {
  const FULL = 24;
  const FLOOR = 8;
  const FULL_AT = 6;
  const FLOOR_AT = 30;
  const n = nodeCount || 0;
  if (n <= FULL_AT) return FULL;
  if (n >= FLOOR_AT) return FLOOR;
  const t = (n - FULL_AT) / (FLOOR_AT - FULL_AT);
  return Math.round(FULL - t * (FULL - FLOOR));
}

function toSankey(transitions) {
  const edges = (transitions || []).filter((t) => t.from_screen !== t.to_screen);

  const ordered = [...edges].sort((a, b) => {
    if (b.count !== a.count) return b.count - a.count;
    if (a.from_screen !== b.from_screen) return a.from_screen < b.from_screen ? -1 : 1;
    return a.to_screen < b.to_screen ? -1 : 1;
  });

  const adj = new Map();
  const kept = [];
  let droppedCount = 0;

  for (const e of ordered) {
    // Adding from->to creates a cycle iff `to` can already reach `from`.
    if (pathExists(adj, e.to_screen, e.from_screen)) {
      droppedCount += 1;
      continue;
    }
    if (!adj.has(e.from_screen)) adj.set(e.from_screen, new Set());
    adj.get(e.from_screen).add(e.to_screen);
    kept.push(e);
  }

  const names = [];
  const indexOf = (name) => {
    const found = names.indexOf(name);
    if (found !== -1) return found;
    names.push(name);
    return names.length - 1;
  };
  const links = kept.map((t) => ({
    source: indexOf(t.from_screen),
    target: indexOf(t.to_screen),
    value: t.count,
  }));
  return { nodes: names.map((name) => ({ name })), links, droppedCount };
}

export default toSankey;
export { sankeyHeight, sankeyNodePadding };
